import * as actionTypes from "../actionTypes";
import * as breakpoints from "../styles/breakpoints";
import createReducer from "../lib/createReducer";
import findIndex from "lodash.findindex";

export const DEFAULT_PAGE_DESCRIPTION = "Docket Navigator";
export const DEFAULT_PAGE_TITLE = "Docket Navigator";

export const defaultState = {
	clientOutdated: false,
	clientUnauthorized: false,
	description: DEFAULT_PAGE_DESCRIPTION,
	errorDialogOpen: false,
	initialRender: true,
	isMobile: false,
	isPortraitTablet: false,
	isTablet: false,
	openDialogs: [],
	scheduledMaintenance: null,
	serviceUnavailable: false,
	siPromise: () => Promise.reject(new Error()),
	title: DEFAULT_PAGE_TITLE,
	toasts: [],
	windowHeight: window.innerHeight,
	windowWidth: window.innerWidth,
};

const PAGE_ADD_DIALOG = (state, action) => {
	const { openTime } = action;
	return { ...state, openDialogs: state.openDialogs.concat(openTime) };
};

const PAGE_REMOVE_DIALOG = (state, action) => {
	const { openTime } = action;
	return {
		...state,
		openDialogs: state.openDialogs.filter(d => d !== openTime),
	};
};

const PAGE_WINDOW_RESIZED = (state, action) => {
	const { windowHeight, windowWidth } = action;
	return {
		...state,
		windowHeight,
		windowWidth,
		isMobile: windowWidth <= breakpoints.maxMobile,
		isTablet: windowWidth <= breakpoints.maxTablet && windowWidth >= breakpoints.maxMobile,
		isPortraitTablet:
			windowWidth <= breakpoints.maxMobile && windowWidth >= breakpoints.minPortraitTablet,
	};
};

const PAGE_SET_ERROR_DIALOG_OPEN = (state, action) => {
	const { open } = action;
	return { ...state, errorDialogOpen: open };
};

const PAGE_SET_DESCRIPTION = (state, action) => {
	const { description } = action;
	return { ...state, description };
};

const PAGE_SET_INITIAL_RENDER = (state, action) => {
	const { initialRender } = action;
	return { ...state, initialRender };
};

const PAGE_SET_SCHEDULED_MAINTENANCE = (state, action) => {
	const { scheduledMaintenance } = action;
	return { ...state, scheduledMaintenance };
};

const PAGE_SET_TOAST = (state, action) => {
	const { message, iconName, displayTime, id, manualDismiss } = action;
	if (!message || !displayTime || !id) {
		return state;
	}

	return {
		...state,
		toasts: [
			...state.toasts,
			{
				displayTime,
				iconName,
				id,
				message,
				manualDismiss,
				visible: false,
			},
		],
	};
};

const PAGE_REMOVE_TOAST = (state, action) => {
	const { id } = action;
	if (!id || !state.toasts.length) {
		return state;
	}

	const toastIndex = findIndex(state.toasts, t => t.id === id);
	if (toastIndex === -1) {
		return state;
	}

	return {
		...state,
		toasts: [...state.toasts.slice(0, toastIndex), ...state.toasts.slice(toastIndex + 1)],
	};
};

const PAGE_REMOVE_TOASTS = (state, action) => {
	const { ids } = action;
	if (!state.toasts.length || !ids.length) {
		return state;
	}

	let nextState = state;
	ids.forEach(id => {
		nextState = PAGE_REMOVE_TOAST(nextState, { id });
	});

	return nextState;
};

const PAGE_SET_TOAST_VISIBLE = (state, action) => {
	const { visible, id } = action;
	if (!state.toasts.length || visible === null || visible === undefined || !id) {
		return state;
	}

	const toastIndex = findIndex(state.toasts, t => t.id === id);
	if (toastIndex === -1) {
		return state;
	}

	return {
		...state,
		toasts: [
			...state.toasts.slice(0, toastIndex),
			{
				...state.toasts[toastIndex],
				visible,
			},
			...state.toasts.slice(toastIndex + 1),
		],
	};
};

const PAGE_SET_TOASTS_VISIBLE = (state, action) => {
	const { visible, ids } = action;
	if (!state.toasts.length || visible === null || visible === undefined || !ids.length) {
		return state;
	}

	let nextState = state;
	ids.forEach(id => {
		nextState = PAGE_SET_TOAST_VISIBLE(nextState, { visible, id });
	});

	return nextState;
};

const PAGE_SET_SERVICE_UNAVAILABLE = (state, action) => {
	const { serviceUnavailable } = action;

	if (serviceUnavailable === null || serviceUnavailable === undefined) {
		return state;
	}

	return {
		...state,
		serviceUnavailable,
	};
};

const PAGE_SET_CLIENT_OUTDATED = (state, action) => {
	const { clientOutdated } = action;
	return {
		...state,
		clientOutdated,
	};
};

const PAGE_SET_CLIENT_UNAUTH = (state, action) => {
	const { clientUnauthorized } = action;
	return {
		...state,
		clientUnauthorized,
	};
};

const PAGE_SET_SI_PROMISE = (state, action) => {
	const { siPromise } = action;
	return {
		...state,
		siPromise,
	};
};

const handlers = {};
handlers[actionTypes.PAGE_ADD_DIALOG] = PAGE_ADD_DIALOG;
handlers[actionTypes.PAGE_REMOVE_DIALOG] = PAGE_REMOVE_DIALOG;
handlers[actionTypes.PAGE_SET_DESCRIPTION] = PAGE_SET_DESCRIPTION;
handlers[actionTypes.PAGE_SET_ERROR_DIALOG_OPEN] = PAGE_SET_ERROR_DIALOG_OPEN;
handlers[actionTypes.PAGE_WINDOW_RESIZED] = PAGE_WINDOW_RESIZED;
handlers[actionTypes.PAGE_SET_INITIAL_RENDER] = PAGE_SET_INITIAL_RENDER;
handlers[actionTypes.PAGE_SET_SCHEDULED_MAINTENANCE] = PAGE_SET_SCHEDULED_MAINTENANCE;
handlers[actionTypes.PAGE_SET_TOAST] = PAGE_SET_TOAST;
handlers[actionTypes.PAGE_REMOVE_TOAST] = PAGE_REMOVE_TOAST;
handlers[actionTypes.PAGE_REMOVE_TOASTS] = PAGE_REMOVE_TOASTS;
handlers[actionTypes.PAGE_SET_TOAST_VISIBLE] = PAGE_SET_TOAST_VISIBLE;
handlers[actionTypes.PAGE_SET_TOASTS_VISIBLE] = PAGE_SET_TOASTS_VISIBLE;
handlers[actionTypes.PAGE_SET_SERVICE_UNAVAILABLE] = PAGE_SET_SERVICE_UNAVAILABLE;
handlers[actionTypes.PAGE_SET_CLIENT_OUTDATED] = PAGE_SET_CLIENT_OUTDATED;
handlers[actionTypes.PAGE_SET_CLIENT_UNAUTH] = PAGE_SET_CLIENT_UNAUTH;
handlers[actionTypes.PAGE_SET_SI_PROMISE] = PAGE_SET_SI_PROMISE;
export default createReducer(defaultState, handlers);
